import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Alert, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Chip, Checkbox, OutlinedInput, ListItemText, FormControlLabel,
  TablePagination
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RetryIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';

import { useAppDispatch, useAppSelector } from '../hooks/redux' //'../hooks/reduxHooks';
import {
  fetchOrderHistory,
  fetchOrderById,
  retryFailedOrder,
  setSelectedOrder as clearSelectedOrder,
} from '../store/slices/orderSlice';
import { fetchAllBranches } from '../store/slices/branchSlice';
import { Order, OrderStatus, CloudOrderState } from '../types/index'; // Ensure enums are imported
import { getOrderStatusText, getOrderStatusColor, canRetryOrder, getCloudStatusText, getCloudStatusColor } from '../utils/helpers'; // Ensure this line is present and correct
import OrderDetailsView from '../components/OrderManagement/OrderDetailsView';
import { signalRService } from '../services/signalRService'; // Re-enabled with disabled stub
import PermissionGate from '../components/Common/PermissionGate';
import { usePermission } from '../hooks/usePermissions';

interface RootState {
  orders: {
    orderHistory: Order[] | null;
    selectedOrder: Order | null;
    loading: boolean;
    error: string | null | any;
  };
  branches: {
    branches: Array<{ id: string; name: string }> | null;
    loading: boolean;
    error: string | null | any;
  };
}



// Settings keys for localStorage
const ORDERS_ROWS_PER_PAGE_KEY = 'orders_rows_per_page'; // Key for saving rows per page
// Day start hour implementation removed

const OrdersPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const { orderHistory, loading, error } = useAppSelector((state: RootState) => state.orders);
  const selectedOrderDetails = useAppSelector((state: RootState) => state.orders.selectedOrder);
  const { branches } = useAppSelector((state: RootState) => state.branches);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<string | ''>('all');
  const [statusFilters, setStatusFilters] = useState<OrderStatus[]>([]);
  const [cloudStateFilters, setCloudStateFilters] = useState<CloudOrderState[]>([]);
  const [filterCashOnly, setFilterCashOnly] = useState<boolean>(false);
  const [filterHasDiscount, setFilterHasDiscount] = useState<boolean>(false);
  const [filterPrepaidOnly, setFilterPrepaidOnly] = useState<boolean>(false);

  // All available POS statuses
  const allStatuses: OrderStatus[] = [
    OrderStatus.New,
    OrderStatus.Pending,
    OrderStatus.SentToPOS,
    OrderStatus.Failed,
    OrderStatus.CancelledInPOS,
    OrderStatus.CancelFailed,
    OrderStatus.ClosedInPOS,
    OrderStatus.CloseFailed,
  ];

  const allCloudStates: CloudOrderState[] = [
    CloudOrderState.NEW,
     CloudOrderState.ACCEPTED,
    CloudOrderState.CLOSED,
    CloudOrderState.CANCELLED,
    CloudOrderState.CANCELLED_BY_TRANSPORT,
    CloudOrderState.CANCELLED_BY_PLATFORM,
    CloudOrderState.CANCELLED_BY_VENDOR,
    
   
    // CloudOrderState.MODIFIED_BY_VENDOR,
    // CloudOrderState.RECONCILIATION_STARTED,
    // CloudOrderState.RECONCILED_WITH_PLATFORM,
    // CloudOrderState.RECONCILED_WITH_TRANSPORT,
    // CloudOrderState.PREORDER_ACCEPTED,
    // CloudOrderState.WAITING_FOR_TRANSPORT,
    
  ];

  // Helper: get today's date
  const getTodayRange = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return {
      startDate: today,
      endDate: today
    };
  }, []);

  // Initialize dates with today's range (midnight to midnight)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Initialize date when component mounts
  useEffect(() => {
    const todayRange = getTodayRange();
    setSelectedDate(todayRange.startDate);
  }, [getTodayRange]);  const [selectedOrderSummary, setSelectedOrderSummary] = useState<Order | null>(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const savedRowsPerPage = localStorage.getItem(ORDERS_ROWS_PER_PAGE_KEY);
    return savedRowsPerPage ? parseInt(savedRowsPerPage, 10) : 5; // Default to 5
  });  // Permission checks
  const canViewOrders = usePermission('orders:read');

  // No longer need to listen for dayStartHour changes
  useEffect(() => {
    // Storage event handling removed as dayStartHour is no longer used
    return () => { /* Cleanup function */ };
  }, []);

  useEffect(() => {
    if (canViewOrders) {
      dispatch(fetchAllBranches());
    }
  }, [dispatch, canViewOrders]);
  const loadOrders = useCallback(() => {
    if (!selectedBranch || !selectedDate) return;

    // Format as exact date (YYYY-MM-DD) with no time expansion
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const d = String(selectedDate.getDate()).padStart(2, '0');
    const dayStr = `${y}-${m}-${d}`;

    dispatch(fetchOrderHistory({
      branchId: selectedBranch === 'all' ? null : selectedBranch,
      dateFrom: dayStr,
      dateTo: dayStr,
    }));
  }, [dispatch, selectedBranch, selectedDate]);
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);  // Reset pagination when search term or filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilters, cloudStateFilters, filterCashOnly, filterHasDiscount, filterPrepaidOnly, selectedBranch, selectedDate]);  useEffect(() => {
    // SignalR disabled for now - using stub implementation
    signalRService.connect();
    // Example: signalRService.on("OrderUpdate", loadOrders);
    return () => {
      signalRService.disconnect();
    };
  }, [loadOrders]);

  const getCurrentOrders = (): Order[] => {
    return orderHistory || [];
  };

  // Calculate first and last cloud short codes for the selected day
  const getDayRange = (): { firstCode: string | null; lastCode: string | null; totalOrders: number } => {
    const orders = getCurrentOrders();
    if (orders.length === 0) {
      return { firstCode: null, lastCode: null, totalOrders: 0 };
    }

    // Sort orders by orderTime to get chronological order
    const sortedOrders = [...orders].sort((a, b) => {
      const dateA = new Date(a.orderTime);
      const dateB = new Date(b.orderTime);
      return dateA.getTime() - dateB.getTime();
    });

    const firstOrder = sortedOrders[0];
    const lastOrder = sortedOrders[sortedOrders.length - 1];

    return {
      firstCode: firstOrder?.cloudShortCode || null,
      lastCode: lastOrder?.cloudShortCode || null,
      totalOrders: orders.length
    };
  };  const filteredOrders = getCurrentOrders().filter((order: Order) => {
    const matchesSearch =
      order.cloudShortCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.localOrderNumber?.toLowerCase().includes(searchTerm.toLowerCase())||
      order.customerName?.toLowerCase().includes(searchTerm.toLowerCase())||
      order.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilters.length === 0 || statusFilters.includes(order.status);
  const matchesCloudState = cloudStateFilters.length === 0 || (
      order.cloudOrderState ? cloudStateFilters.includes(order.cloudOrderState as CloudOrderState) : false
    );
  const matchesCashOnly = !filterCashOnly || order.isPaid === false;
  const matchesHasDiscount = !filterHasDiscount || ((order.discounts && order.discounts.length > 0) || false);
  const matchesPrepaidOnly = !filterPrepaidOnly || order.isPaid === true;
  return matchesSearch && matchesStatus && matchesCloudState && matchesCashOnly && matchesHasDiscount && matchesPrepaidOnly;
  });

  // Ensure page doesn't exceed available pages
  const maxPage = Math.max(0, Math.ceil(filteredOrders.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, maxPage);

  // Get paginated orders
  const paginatedOrders = filteredOrders.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    localStorage.setItem(ORDERS_ROWS_PER_PAGE_KEY, newRowsPerPage.toString()); // Save to localStorage
  };  const handleRetryOrder = async (orderId: string) => {
    try {
      await dispatch(retryFailedOrder(orderId)).unwrap();
      
      // Refresh the order list
      loadOrders();
      
      // Order details will be updated automatically via store
    } catch (err: any) {
      // Error notifications are now handled in the thunk, so we just log and refresh
      console.error('Error retrying order:', err);
      loadOrders(); // Still refresh to get latest state
    }
  };

  const handleViewOrderDetails = (order: Order) => {
    setSelectedOrderSummary(order);
    dispatch(clearSelectedOrder(null));
    setOrderDetailsOpen(true);
    dispatch(fetchOrderById(order.id));
  };  const getBranchName = (branchId: string) => {
    const branch = branches?.find((b: { id: string; name: string }) => b.id === branchId);
    return branch?.name || 'Unknown Branch';
  };
  const dialogOrder = selectedOrderDetails || selectedOrderSummary;

  const OrderTable = ({ orders, onRetryOrder, onViewDetails }: {
    orders: Order[];
    onRetryOrder: (orderId: string) => void;
    onViewDetails: (order: Order) => void;
  }) => {
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 'none' }}> {/* MODIFIED HERE */}
        <Table stickyHeader aria-label="orders table">          <TableHead>            <TableRow>
              <TableCell>Cloud</TableCell>
              <TableCell>POS</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>POS Status</TableCell>
              <TableCell>Cloud Status</TableCell>
              <TableCell>Date</TableCell>              
              <TableCell>Total Items</TableCell> {/* Added */}
              {/* <TableCell>Total</TableCell> */}
              <TableCell>Payment</TableCell> {/* Changed from Paid */}
              <TableCell>Branch</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>            {loading && orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center"> {/* Updated colSpan from 9 to 10 */}
                  <CircularProgress />
                  <Typography sx={{ mt: 1 }}>Loading orders...</Typography>
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center"> {/* Updated colSpan from 9 to 10 */}
                  <Typography>No orders found for the selected criteria.</Typography>
                  {!selectedBranch && <Typography variant="caption" color="text.secondary" display="block">Please select a branch or choose "All Branches".</Typography>}
                  {selectedBranch && !selectedDate && <Typography variant="caption" color="text.secondary" display="block">Please select a date.</Typography>}
                </TableCell>
              </TableRow>
            ) : (              orders.map((order) => (
                <TableRow hover key={order.id}>
                  <TableCell>{order.cloudShortCode || 'N/A'}</TableCell>
                  <TableCell>{order.localOrderNumber || 'N/A'}</TableCell>
                  <TableCell>{order.customerName || 'N/A'}</TableCell>                  
                  <TableCell>
                    <Chip
                      label={getOrderStatusText(order.status)}
                      color={getOrderStatusColor(order.status)}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>                  
                  <TableCell>
                    <Tooltip title={`Cloud Status: ${order.cloudOrderState || 'Unknown'}`}>
                      <Chip
                        label={getCloudStatusText(order.cloudOrderState)}
                        color={getCloudStatusColor(order.cloudOrderState)}
                        size="small"
                         onClick={(e) => e.stopPropagation()}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{new Date(order.orderTime).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</TableCell>                  
                  <TableCell>{order.totalItems}</TableCell>                  
                  {/* <TableCell>{order.total?.toFixed(2) || '0.00'}</TableCell>*/}                 
                  <TableCell>
                    {(order.paymentMethod || 'N/A').length > 5 ? (
                      <Tooltip title={order.paymentMethod || 'N/A'}>
                        <span style={{ cursor: 'help' }}>
                          {`${(order.paymentMethod || 'N/A').substring(0, 5)}...`}
                        </span>
                      </Tooltip>
                    ) : (
                      order.paymentMethod || 'N/A'
                    )}
                  </TableCell>
                  <TableCell>{getBranchName(order.branchId)}</TableCell><TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton onClick={() => onViewDetails(order)} size="small">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>                    {canRetryOrder(order.status) && (
                      <PermissionGate permissions={["orders:manage"]}>
                        <Tooltip title="Retry Order">
                          <IconButton onClick={() => onRetryOrder(order.id)} size="small" color="warning">
                            <RetryIcon />
                          </IconButton>
                        </Tooltip>
                      </PermissionGate>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PermissionGate permission="orders:read" fallback={
        <Alert severity="error" sx={{ mt: 2 }}>
          You don't have permission to view orders.
        </Alert>
      }>
        <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 }, pt: { xs: 0.5, sm: 1, md: 1.5 } }}> {/* MODIFIED HERE: Removed maxHeight and overflowY */}
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {typeof error === 'string' ? error : 'An unexpected error occurred. Please check console for details.'}
          </Alert>
        )}

        {/* Filters Card */}
        <Card sx={{ mb: 1.5 }} variant="outlined">
          <CardContent sx={{ pb: '12px !important' }}>
            {/* <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>Filters</Typography> */}
            {selectedDate && (() => {
              const dayRange = getDayRange();
              return dayRange.totalOrders > 0 ? (
                <Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
                  <Typography variant="body2">
                    <strong>Day Range:</strong> {dayRange.totalOrders} order(s) from #{dayRange.firstCode} to #{dayRange.lastCode}
                    {selectedDate && (
                      <span> on {selectedDate.toLocaleDateString('en-US', { 
                        weekday: 'short',
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}</span>
                    )}
                    {/* Day start hour removed */}
                  </Typography>
                </Alert>
              ) : null;
            })()}
            {/* Row 1: Search + Branch + Date + Actions */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'column', md: 'row' },
              flexWrap: 'wrap', 
              gap: 1.5, 
              alignItems: { xs: 'stretch', sm: 'stretch', md: 'center' }, 
              mb: 1.5 
            }}>
              <TextField
                sx={{ 
                  flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 auto' },
                  width: { xs: '100%', sm: '100%', md: 300 },
                  minWidth: { xs: 'auto', sm: 'auto', md: 300 },
                  maxWidth: { xs: 'none', sm: 'none', md: 300 },
                  order: { xs: 1, sm: 1, md: 1 }
                }}
                placeholder="Search ID, Customer Name, Phone"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                variant="outlined"
                size="small"
              />
              <FormControl sx={{ 
                flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto' },
                width: { xs: '100%', sm: '100%', md: 300 }, 
                minWidth: { xs: 'auto', sm: 'auto', md: 300 }, 
                maxWidth: { xs: 'none', sm: 'none', md: 300 },
                order: { xs: 2, sm: 2, md: 2 }
              }} size="small" variant="outlined">
                <InputLabel>Branch</InputLabel>
                <Select
                  size="small"
                  value={selectedBranch}
                  label="Branch"
                  onChange={(e) => {
                    setSelectedBranch(e.target.value as string);
                  }}
                >
                  <MenuItem value="all">All Branches</MenuItem>
                  <MenuItem value=""><em>Select Branch</em></MenuItem>
                  {(branches || []).map((branch: { id: string; name: string }) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center',
                flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto' },
                justifyContent: { xs: 'space-between', sm: 'space-between', md: 'flex-end' },
                order: { xs: 3, sm: 3, md: 3 }
              }}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={(newValue: Date | null) => {
                    if (newValue) {
                      const adjustedDate = new Date(newValue.getFullYear(), newValue.getMonth(), newValue.getDate());
                      setSelectedDate(adjustedDate);
                    } else {
                      setSelectedDate(null);
                    }
                  }}
                  slotProps={{ 
                    textField: { 
                      size: 'small', 
                      variant: 'outlined', 
                      sx: { 
                        width: { xs: 'auto', sm: 'auto', md: 200 }, 
                        flex: { xs: 1, sm: 1, md: 'none' }
                      }
                    } 
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const todayRange = getTodayRange();
                    setSelectedDate(todayRange.startDate);
                  }}
                  sx={{ minWidth: 'auto', px: 2, flexShrink: 0 }}
                  title="Set date to today"
                >
                  Today
                </Button>
              </Box>
            </Box>

            {/* Row 2: Cloud State + Status + Utilities */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'column', md: 'row' },
              flexWrap: 'wrap', 
              gap: 1.5, 
              alignItems: { xs: 'stretch', sm: 'stretch', md: 'center' }, 
              justifyContent: 'flex-start' 
            }}>

              {/* Cloud State multi-select */}
              <FormControl sx={{ 
                flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto' },
                width: { xs: '100%', sm: '100%', md: 300 },
                minWidth: { xs: 'auto', sm: 'auto', md: 300 }, 
                maxWidth: { xs: 'none', sm: 'none', md: 300 },
                order: { xs: 1, sm: 1, md: 1 }
              }} size="small" variant="outlined">
                <InputLabel>Cloud State</InputLabel>
                <Select
                  multiple
                  size="small"
                  value={cloudStateFilters as unknown as string[]}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    setCloudStateFilters(value as CloudOrderState[]);
                  }}
                  input={<OutlinedInput label="Cloud State" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((s) => (
                        <Chip key={s} label={getCloudStatusText(s as CloudOrderState)} size="small" color={getCloudStatusColor(s as CloudOrderState)} />
                      ))}
                    </Box>
                  )}
                >
                  {allCloudStates.map((state) => (
                    <MenuItem key={state} value={state}>
                      <Checkbox checked={cloudStateFilters.includes(state)} />
                      <ListItemText primary={getCloudStatusText(state)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Status multi-select */}
              <FormControl sx={{ 
                flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto' },
                width: { xs: '100%', sm: '100%', md: 300 }, 
                minWidth: { xs: 'auto', sm: 'auto', md: 300 }, 
                maxWidth: { xs: 'none', sm: 'none', md: 300 },
                order: { xs: 2, sm: 2, md: 2 }
              }} size="small" variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  size="small"
                  value={statusFilters as unknown as number[]}
                  onChange={(e) => {
                    const value = e.target.value as number[];
                    setStatusFilters(value as unknown as OrderStatus[]);
                  }}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((s) => (
                        <Chip key={s} label={getOrderStatusText(s as OrderStatus)} size="small" color={getOrderStatusColor(s as OrderStatus)} />
                      ))}
                    </Box>
                  )}
                >
                  {allStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={statusFilters.includes(status)} />
                      <ListItemText primary={getOrderStatusText(status)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Independent toggles */}
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                alignItems: 'center', 
                gap: { xs: 0.5, sm: 0.5, md: 1 },
                flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto' },
                justifyContent: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
                order: { xs: 3, sm: 3, md: 3 }
              }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={filterCashOnly} onChange={(e) => setFilterCashOnly(e.target.checked)} />}
                  label="Cash Only"
                  sx={{ ml: 0.5, mr: { xs: 1, sm: 1, md: 2 } }}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={filterHasDiscount} onChange={(e) => setFilterHasDiscount(e.target.checked)} />}
                  label="Has Discount"
                  sx={{ mr: { xs: 1, sm: 1, md: 2 } }}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={filterPrepaidOnly} onChange={(e) => setFilterPrepaidOnly(e.target.checked)} />}
                  label="Prepaid Only"
                />
              </Box>

              {/* Spacer - only on larger screens */}
              <Box sx={{ 
                flex: 1, 
                display: { xs: 'none', sm: 'none', md: 'block' },
                order: 4
              }} />

              {/* Utilities: Clear + Refresh + Count */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'row', md: 'row' },
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5, md: 1 },
                flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto' },
                justifyContent: { xs: 'space-between', sm: 'flex-start', md: 'flex-end' },
                order: { xs: 4, sm: 4, md: 5 }
              }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedBranch('all');
                    setStatusFilters([]);
                    setCloudStateFilters([]);
                    setFilterCashOnly(false);
                    setFilterHasDiscount(false);
                    setFilterPrepaidOnly(false);
                  }}
                  sx={{ minWidth: { xs: 'auto', sm: 'auto', md: 120 } }}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  onClick={loadOrders}
                  startIcon={<RefreshIcon />}
                  disabled={loading || !selectedBranch || !selectedDate}
                  size="small"
                  sx={{ minWidth: { xs: 100, sm: 120, md: 120 } }}
                >
                  Refresh
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  flex: '0 0 auto', 
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', sm: 'block', md: 'block' },
                  order: { xs: 3, sm: 3, md: 3 }
                }}>
                  {filteredOrders.length} order(s)
                  {filteredOrders.length > rowsPerPage && (
                    <span> • Showing {Math.min(rowsPerPage, filteredOrders.length - currentPage * rowsPerPage)} of {filteredOrders.length}</span>
                  )}
                </Typography>
              </Box>
              
              {/* Order count for mobile */}
              <Typography variant="body2" color="text.secondary" sx={{ 
                display: { xs: 'block', sm: 'none', md: 'none' },
                textAlign: 'center',
                mt: 1,
                order: 5
              }}>
                {filteredOrders.length} order(s)
                {filteredOrders.length > rowsPerPage && (
                  <span> • Showing {Math.min(rowsPerPage, filteredOrders.length - currentPage * rowsPerPage)} of {filteredOrders.length}</span>
                )}
              </Typography>
            </Box>
          </CardContent>
        </Card>        {/* Pagination */}
        {filteredOrders.length > 0 && (
          <Card sx={{ mb: 1 }} variant="outlined">
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              page={currentPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
              sx={{ borderBottom: 'none', py: 0.5 }}
            />
          </Card>
        )}

        {/* Removed Tabs and TabPanels for different order categories */}
        {/* Display a single OrderTable for all fetched orders */}        <Card variant="outlined">
          <OrderTable 
            orders={paginatedOrders} 
            onRetryOrder={handleRetryOrder}
            onViewDetails={handleViewOrderDetails} 
          />
        </Card>

        {/* Order Details Dialog */}
        <Dialog
          open={orderDetailsOpen}
          onClose={() => {
            setOrderDetailsOpen(false);
            setSelectedOrderSummary(null);
            dispatch(clearSelectedOrder(null));
          }}
          maxWidth="lg"
          fullWidth
        >          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                Order Details - #{dialogOrder?.cloudShortCode}
              </Typography>
              {dialogOrder && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={getOrderStatusText(dialogOrder.status)}
                    size="small"
                    color={getOrderStatusColor(dialogOrder.status)}
                    sx={{ fontWeight: 'bold' }}
                  />
                  {dialogOrder.cloudOrderState && (
                    <Chip
                      label={getCloudStatusText(dialogOrder.cloudOrderState)}
                      size="small"
                      color={getCloudStatusColor(dialogOrder.cloudOrderState)}
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => {
                setOrderDetailsOpen(false);
                setSelectedOrderSummary(null);
                dispatch(clearSelectedOrder(null));
              }}
              sx={{color: (theme) => theme.palette.grey[500]}}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>          <DialogContent dividers sx={{ bgcolor: (theme) => theme.palette.background.default }}>
            {selectedOrderDetails ? (              <OrderDetailsView 
                order={selectedOrderDetails} 
                showHeader={false}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>          <DialogActions sx={{ px: 3, py: 2, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Button onClick={() => {
              setOrderDetailsOpen(false);
              setSelectedOrderSummary(null);
              dispatch(clearSelectedOrder(null));
            }} color="inherit">Close</Button>
          </DialogActions>
        </Dialog>
        </Box>
      </PermissionGate>
    </LocalizationProvider>
  );
};

export default OrdersPage;
